import { Mic, Sparkles, Trash2, RotateCcw, FileAudio } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAiStatus } from "@/hooks/useAiStatus";
import { formatClock } from "@/hooks/useAudioRecorder";
import { fileUrl } from "@/lib/files";
import { cn } from "@/lib/utils";

const recorderSupported = () =>
  typeof navigator !== "undefined" && !!navigator.mediaDevices?.getUserMedia && typeof MediaRecorder !== "undefined";

/**
 * Entry card at the top of the report forms.
 *  - no note yet  -> dark hero card with a big «Start opptak» button
 *  - note attached -> compact strip with player, re-record and remove
 * Hidden entirely when AI is disabled or the browser cannot record.
 */
export const VoiceReportCard = ({ note, onStart, onRemove, className, testid = "voice-report-card" }) => {
  const ai = useAiStatus();
  if (!ai?.features?.voice_report || !recorderSupported()) return null;

  if (note?.file_id) {
    return (
      <div className={cn("flex flex-wrap items-center gap-3 rounded-[var(--radius)] border bg-card p-3 shadow-sm", className)} data-testid={`${testid}-attached`}>
        <span className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-primary/15 text-primary"><FileAudio className="h-5 w-5" /></span>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold">Lydnotat lagt ved{note.duration_s ? ` · ${formatClock(note.duration_s)}` : ""}</div>
          <div className="truncate text-xs text-muted-foreground">{note.summary || "Rapporten er fylt ut frå opptaket – kontroller felta."}</div>
        </div>
        <audio controls preload="none" src={fileUrl(note.file_id)} className="h-9 w-full sm:w-64" data-testid={`${testid}-player`} />
        <div className="flex gap-1">
          <Button type="button" variant="ghost" size="sm" onClick={onStart} data-testid={`${testid}-rerecord`}><RotateCcw /> Nytt opptak</Button>
          <Button type="button" variant="ghost" size="iconSm" onClick={onRemove} aria-label="Fjern lydnotat" className="text-muted-foreground hover:text-destructive" data-testid={`${testid}-remove`}><Trash2 className="h-4 w-4" /></Button>
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        "relative overflow-hidden rounded-[var(--radius)] bg-[#0B0D10] p-4 text-white shadow-sm dark:bg-white dark:text-[#0B0D10] sm:p-5",
        className
      )}
      data-testid={testid}
    >
      <div className="pointer-events-none absolute -right-10 -top-10 h-40 w-40 rounded-full bg-primary/25 blur-2xl" aria-hidden="true" />
      <div className="relative flex flex-col gap-4 sm:flex-row sm:items-center">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider opacity-70">
            <Sparkles className="h-3.5 w-3.5 text-primary" /> Raskaste måten
          </div>
          <h2 className="mt-1 font-heading text-lg font-bold leading-tight sm:text-xl">Snakk inn rapporten</h2>
          <p className="mt-1 text-sm opacity-75">Fortel kva du har gjort – AI skriv ned og fyller ut maskin, arbeid, avvik og neste service. Du kontrollerer alt før lagring.</p>
        </div>
        <Button
          type="button"
          onClick={onStart}
          size="lg"
          className="h-14 shrink-0 gap-2 rounded-full px-6 text-base shadow-[0_12px_30px_rgba(255,78,0,0.35)] sm:h-12"
          data-testid={`${testid}-start`}
        >
          <Mic className="h-5 w-5" /> Start opptak
        </Button>
      </div>
    </div>
  );
};
